
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { RefreshCw, Check, X, AlertCircle } from "lucide-react";

// Mock data for sync status
const syncLogs = [
  { 
    id: 1, 
    timestamp: "2025-05-11 09:15:22", 
    system: "POS",
    action: "Upload", 
    status: "success", 
    itemsProcessed: 42, 
    details: "All products synchronized successfully" 
  },
  { 
    id: 2, 
    timestamp: "2025-05-11 08:30:15", 
    system: "JimatShop",
    action: "Upload", 
    status: "partial", 
    itemsProcessed: 38, 
    details: "3 products failed to sync due to missing images" 
  },
  { 
    id: 3, 
    timestamp: "2025-05-10 17:45:33", 
    system: "POS",
    action: "Download", 
    status: "success", 
    itemsProcessed: 12, 
    details: "Inventory updates received successfully" 
  },
  { 
    id: 4, 
    timestamp: "2025-05-10 15:10:05", 
    system: "JimatShop",
    action: "Upload", 
    status: "error", 
    itemsProcessed: 0, 
    details: "Connection error: Could not establish connection to server" 
  },
  { 
    id: 5, 
    timestamp: "2025-05-10 10:22:18", 
    system: "POS",
    action: "Upload", 
    status: "success", 
    itemsProcessed: 5, 
    details: "New products synchronized successfully" 
  }
];

const SyncStatusTab: React.FC = () => {
  const [isPosSyncing, setIsPosSyncing] = useState(false);
  const [isJimatShopSyncing, setIsJimatShopSyncing] = useState(false);
  
  const handlePosSync = () => {
    setIsPosSyncing(true);
    // Simulate sync process
    setTimeout(() => {
      setIsPosSyncing(false);
    }, 3000);
  };
  
  const handleJimatShopSync = () => {
    setIsJimatShopSyncing(true);
    // Simulate sync process
    setTimeout(() => {
      setIsJimatShopSyncing(false);
    }, 3000);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* POS Sync Status */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle>POS System</CardTitle>
            <CardDescription>
              Last synchronized: 2 hours ago
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm">Products:</span>
                <span className="text-sm font-medium">245</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm">Categories:</span>
                <span className="text-sm font-medium">12</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm">Sync Status:</span>
                <span className="text-sm font-medium flex items-center">
                  <Check className="h-4 w-4 text-green-500 mr-1" />
                  Connected
                </span>
              </div>
            </div>
          </CardContent>
          <CardFooter>
            <Button 
              className="w-full"
              onClick={handlePosSync}
              disabled={isPosSyncing}
            >
              {isPosSyncing ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Syncing...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Sync Now
                </>
              )}
            </Button>
          </CardFooter>
        </Card>

        {/* JimatShop Sync Status */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle>JimatShop</CardTitle>
            <CardDescription>
              Last synchronized: 3 hours ago
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm">Products:</span>
                <span className="text-sm font-medium">198</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm">Categories:</span>
                <span className="text-sm font-medium">8</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm">Sync Status:</span>
                <span className="text-sm font-medium flex items-center">
                  <AlertCircle className="h-4 w-4 text-amber-500 mr-1" />
                  Partial Sync
                </span>
              </div>
            </div>
          </CardContent>
          <CardFooter>
            <Button 
              className="w-full"
              onClick={handleJimatShopSync}
              disabled={isJimatShopSyncing}
            >
              {isJimatShopSyncing ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Syncing...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Sync Now
                </>
              )}
            </Button>
          </CardFooter>
        </Card>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-medium">Sync History</h3>
        <div className="border rounded-md overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date & Time</TableHead>
                <TableHead>System</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Items</TableHead>
                <TableHead>Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {syncLogs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell>{log.timestamp}</TableCell>
                  <TableCell>{log.system}</TableCell>
                  <TableCell>{log.action}</TableCell>
                  <TableCell>
                    {log.status === "success" && (
                      <span className="flex items-center text-green-500">
                        <Check className="h-4 w-4 mr-1" />
                        Success
                      </span>
                    )}
                    {log.status === "error" && (
                      <span className="flex items-center text-red-500">
                        <X className="h-4 w-4 mr-1" />
                        Failed
                      </span>
                    )}
                    {log.status === "partial" && (
                      <span className="flex items-center text-amber-500">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        Partial
                      </span>
                    )}
                  </TableCell>
                  <TableCell>{log.itemsProcessed}</TableCell>
                  <TableCell className="max-w-xs truncate" title={log.details}>
                    {log.details}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};

export default SyncStatusTab;
